/**
 * InlinePrototypeCounters — single combined Early Access counter pill.
 *
 * One shared pool: the first 100,000 Early Access members get a 1-Year
 * Premium Trial + Early Access pricing. Account type (Personal /
 * Business / Government) determines features and onboarding capacity — NOT
 * the Early Access slot count. There are no separate per-type quotas.
 *
 * Data source: GET /api/early-access/counts (via useEarlyAccessCounts) →
 * the SINGLE shared 100,000-member pool. `remaining` ticks down as people
 * actually join, and the dropdown shows how those sign-ups break down across
 * Personal / Business / Government.
 *
 * The dropdown lists the three account types as choices (links), each with a
 * short capability note AND a live count of how many of that type have joined.
 */
import { useLayoutEffect, useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { Users, Building2, Shield, ChevronDown, Activity, ArrowRight } from 'lucide-react';
import { useEarlyAccessCounts } from '@/lib/useEarlyAccessCounts';

const PANEL_WIDTH_SM = 340;
const PANEL_WIDTH_LG = 380;
const VIEWPORT_PADDING = 12;
const TRIGGER_GAP = 6;

const fmt = (n) => (n || 0).toLocaleString();

function TypeRow({ Icon, name, sub, accent, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full group flex items-center gap-3 px-3.5 py-3 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all text-left"
      data-testid={testid}
    >
      <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent.iconBg}`}>
        <Icon className={`w-4 h-4 ${accent.iconText}`} strokeWidth={2.4} />
      </span>
      <div className="flex-1 min-w-0">
        <span className={`text-[12px] font-bold ${accent.label}`}>{name}</span>
        <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{sub}</div>
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" strokeWidth={2.4} />
    </button>
  );
}

export default function InlinePrototypeCounters() {
  const counter = useEarlyAccessCounts();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const ja = lang === 'ja';

  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0, width: PANEL_WIDTH_SM, visible: false });
  const triggerWrapRef = useRef(null);
  const pillRef = useRef(null);
  const panelRef = useRef(null);
  const closeTimer = useRef(null);

  const openPanel = useCallback(() => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
  }, []);
  const schedClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 160);
  }, []);

  const recompute = useCallback(() => {
    const el = pillRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = vw >= 640 ? PANEL_WIDTH_LG : PANEL_WIDTH_SM;
    let left = r.right - width;
    if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
    if (left + width > vw - VIEWPORT_PADDING) left = Math.max(VIEWPORT_PADDING, vw - width - VIEWPORT_PADDING);
    const panelEl = panelRef.current;
    const estHeight = (panelEl ? panelEl.offsetHeight : 0) || 320;
    const spaceBelow = vh - r.bottom - VIEWPORT_PADDING;
    const spaceAbove = r.top - VIEWPORT_PADDING;
    let top;
    if (estHeight + TRIGGER_GAP <= spaceBelow) top = r.bottom + TRIGGER_GAP;
    else if (estHeight + TRIGGER_GAP <= spaceAbove) top = r.top - TRIGGER_GAP - estHeight;
    else top = Math.max(VIEWPORT_PADDING, vh - estHeight - VIEWPORT_PADDING);
    setPosition({ left, top, width, visible: true });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    recompute();
    const id = requestAnimationFrame(recompute);
    return () => cancelAnimationFrame(id);
  }, [open, recompute]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => recompute();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, recompute]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const inTrigger = triggerWrapRef.current && triggerWrapRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (counter.loading) return null;

  const remaining = counter.remaining;
  const limit = counter.limit || 100_000;
  const claimed = counter.claimed || 0;
  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <div
      ref={triggerWrapRef}
      className="relative flex items-center gap-1.5"
      onMouseEnter={openPanel}
      onMouseLeave={schedClose}
      data-testid="inline-prototype-counters"
    >
      <button
        ref={pillRef}
        type="button"
        onClick={() => (open ? setOpen(false) : openPanel())}
        onFocus={openPanel}
        aria-expanded={open}
        aria-haspopup="true"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border bg-slate-50 border-slate-200 hover:bg-white hover:border-slate-300 text-[10.5px] font-bold text-slate-800 transition-all cursor-pointer"
        data-testid="live-tracker-pill"
      >
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
        </span>
        <Activity className="w-3 h-3 text-slate-700" strokeWidth={2.5} />
        <span className="leading-none uppercase tracking-wider">
          {ja ? '先行受付枠' : 'Early Access'}
        </span>
        <span className="inline-flex items-baseline gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500 text-white shadow-sm ring-1 ring-emerald-600/40">
          <span className="leading-none font-mono tabular-nums font-black text-[11.5px]">{fmt(remaining)}</span>
          <span className="leading-none text-[8px] font-bold uppercase tracking-wide opacity-90">{ja ? '残り' : 'left'}</span>
        </span>
        <ChevronDown className={`w-3 h-3 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={2.5} />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className="fixed z-[9999] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden max-h-[calc(100vh-24px)] overflow-y-auto"
          style={{ left: position.left, top: position.top, width: position.width, visibility: position.visible ? 'visible' : 'hidden' }}
          onMouseEnter={openPanel}
          onMouseLeave={schedClose}
          data-testid="live-tracker-panel"
          role="menu"
        >
          {/* Header strip — single combined counter */}
          <div className="px-4 py-3 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              <span className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-300">
                {ja ? '先行受付会員' : 'Early Access members'}
              </span>
              <span className="ml-auto text-[10px] font-mono tabular-nums text-white/70">
                {fmt(remaining)} / {fmt(limit)} {ja ? '残り' : 'left'}
              </span>
            </div>
            <p className="text-[11px] text-white/80 mt-1 leading-snug">
              {ja
                ? `先着${fmt(limit)}名の先行受付会員に、1年間プレミアム無料体験と先行受付料金をご提供します。`
                : `The first ${fmt(limit)} Early Access members get a 1-Year Premium Trial and Early Access pricing.`}
            </p>
            {/* Live claimed/remaining split */}
            <div className="mt-2 flex items-center gap-3" data-testid="live-tracker-claimed-split">
              <div className="flex items-baseline gap-1">
                <span className="text-[15px] font-black tabular-nums text-white leading-none">{fmt(claimed)}</span>
                <span className="text-[9px] font-semibold uppercase tracking-wide text-white/55">{ja ? '登録済み' : 'joined'}</span>
              </div>
              <span className="text-white/25">·</span>
              <div className="flex items-baseline gap-1">
                <span className="text-[15px] font-black tabular-nums text-emerald-300 leading-none">{fmt(remaining)}</span>
                <span className="text-[9px] font-semibold uppercase tracking-wide text-white/55">{ja ? '残り' : 'left'}</span>
              </div>
            </div>
          </div>

          {/* Account types — choices (no public per-type quota counts) */}
          <div className="p-2 space-y-1">
            <div className="px-2 pt-1 pb-1 flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">
                {ja ? 'アカウントタイプを選択' : 'Choose your account type'}
              </span>
            </div>
            <TypeRow
              Icon={Users}
              name={ja ? '個人' : 'Personal'}
              sub={ja ? 'ご家族・個人ドライバー · ETCカード最大10枚' : 'Individuals & families · up to 10 ETC cards'}
              accent={{ iconBg: 'bg-emerald-100', iconText: 'text-emerald-700', label: 'text-emerald-800' }}
              onClick={() => go(`/${lang}/personal/pricing`)}
              testid="live-tracker-row-personal"
            />
            <TypeRow
              Icon={Building2}
              name={ja ? '法人（Business）' : 'Business'}
              sub={ja ? '組織機能 · 体験中は無制限にオンボード' : 'Organization features · unlimited onboarding in trial'}
              accent={{ iconBg: 'bg-purple-100', iconText: 'text-[#7a00a8]', label: 'text-[#5b0080]' }}
              onClick={() => go(`/${lang}/business/pricing`)}
              testid="live-tracker-row-business"
            />
            <TypeRow
              Icon={Shield}
              name={ja ? '官公庁・公共部門' : 'Gov & Public-Sector'}
              sub={ja ? '官公庁機能 · 体験中は無制限にオンボード' : 'Government features · unlimited onboarding in trial'}
              accent={{ iconBg: 'bg-sky-100', iconText: 'text-sky-800', label: 'text-[#04244a]' }}
              onClick={() => go(`/${lang}/government/pricing`)}
              testid="live-tracker-row-government"
            />
          </div>

          {/* Footer note */}
          <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50">
            <p className="text-[10px] text-slate-500 leading-snug">
              {ja
                ? 'アカウントタイプは機能とオンボード容量を決めるもので、先行受付枠の消費数には影響しません。クレジットカード登録不要。'
                : 'Your account type determines features and onboarding capacity — not how many Early Access slots are used. No credit card required.'}
            </p>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
